import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCurrentUser,
  getResolvedConfig,
  saveConfig,
  listUsers,
  listTenants,
  getUser,
  createUser as createUserRequest,
  updateUser,
  deleteUser as deleteUserRequest,
  addUserToTenant,
  removeUserFromTenant,
} from '../api/users';
import { listAuditEvents } from '../api/audit';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import {
  LayoutDashboard,
  Users,
  Settings,
  Activity,
  Save,
  Plus,
  Trash2,
  Shield,
  ShieldCheck,
  AppWindow,
  Network,
  ClipboardList,
  Bell,
  Lock,
} from 'lucide-react';

type UserCenterTab =
  | 'overview'
  | 'auth'
  | 'identity'
  | 'apps'
  | 'organization'
  | 'audit'
  | 'notifications'
  | 'security'
  | 'settings';

interface TenantMembership {
  tenant_id: string;
  role: string;
  tenant_name?: string;
}

interface User {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tenants?: TenantMembership[];
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface SystemSettings {
  platform_name: string;
  default_language: string;
  allow_registration: boolean;
}

interface Application {
  id: string;
  name: string;
  client_id: string;
  redirect_uri: string;
  created_at: string;
}

const TABS: { key: UserCenterTab; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: '平台概览', icon: LayoutDashboard },
  { key: 'identity', label: '身份治理', icon: Shield },
  { key: 'auth', label: '认证与授权', icon: ShieldCheck },
  { key: 'apps', label: '应用管理', icon: AppWindow },
  { key: 'organization', label: '组织架构', icon: Network },
  { key: 'audit', label: '审计日志', icon: ClipboardList },
  { key: 'notifications', label: '消息通知', icon: Bell },
  { key: 'security', label: '安全设置', icon: Lock },
  { key: 'settings', label: '全局设置', icon: Settings },
];

const ROLES = ['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'];

const DEFAULT_SETTINGS: SystemSettings = {
  platform_name: 'Atelier 琢玉坊',
  default_language: 'zh',
  allow_registration: false,
};

const VALID_TABS: UserCenterTab[] = [
  'overview',
  'identity',
  'auth',
  'apps',
  'organization',
  'audit',
  'notifications',
  'security',
  'settings',
];

function isValidTab(value: string | undefined): value is UserCenterTab {
  return !!value && (VALID_TABS as string[]).includes(value);
}

/** 提取后端错误文案（detail 优先，其次 Error.message） */
const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

// ------------------------------------------------------------------
// 用户级配置持久化（复用 /configs 的 USER scope，scope_id = 当前用户 ID）
// ------------------------------------------------------------------
async function fetchCurrentUserId(): Promise<string> {
  const me = await getCurrentUser();
  return me.id;
}

/** 读取当前用户在 USER scope 下的配置；未保存过返回 null */
async function loadUserConfig<T>(key: string, userId: string): Promise<T | null> {
  return getResolvedConfig<T>(key, { user_id: userId });
}

async function saveUserConfig(key: string, value: object, userId: string, description: string) {
  await saveConfig({ key, value, scope: 'user', scope_id: userId, description });
}

export default function UserAdmin() {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<UserCenterTab>(
    isValidTab(tab) ? tab : 'overview'
  );

  // URL tab 参数变化时在渲染期同步 activeTab（官方推荐的 adjust-state-during-render 模式）
  const [prevTab, setPrevTab] = useState(tab);
  if (prevTab !== tab) {
    setPrevTab(tab);
    if (isValidTab(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1>用户中心</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              className={activeTab === item.key ? 'primary' : 'secondary'}
              style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => navigate(`/user-center/${item.key}`)}
            >
              <Icon size={16} />
              {item.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && <PlatformOverview />}
      {activeTab === 'identity' && <IdentityGovernance />}
      {activeTab === 'auth' && <AuthManagement />}
      {activeTab === 'apps' && <AppManagement />}
      {activeTab === 'organization' && <OrganizationStructure />}
      {activeTab === 'audit' && <AuditLogs />}
      {activeTab === 'notifications' && <NotificationSettings />}
      {activeTab === 'security' && <SecuritySettings />}
      {activeTab === 'settings' && <GlobalSettings />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 平台概览
// ═══════════════════════════════════════════════════════════════════
function PlatformOverview() {
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listUsers(), listTenants()])
      .then(([u, t]) => {
        setUsers(u);
        setTenants(t);
      })
      .catch((e) => console.error('Failed to load overview:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const activeUsers = users.filter((u) => u.is_active).length;
  const inactiveUsers = users.length - activeUsers;
  const recentUsers = [...users]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  return (
    <div className="flex flex-col gap-4">
      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}
      >
        <StatCard icon={Users} label="总用户数" value={users.length} />
        <StatCard icon={Activity} label="活跃用户" value={activeUsers} variant="success" />
        <StatCard icon={Users} label="停用用户" value={inactiveUsers} variant="error" />
        <StatCard icon={Shield} label="租户数量" value={tenants.length} variant="info" />
      </div>

      <div className="card">
        <h3 className="mb-3">最近加入的用户</h3>
        {recentUsers.length === 0 ? (
          <p className="text-muted">暂无用户</p>
        ) : (
          <div className="list">
            {recentUsers.map((user) => (
              <div key={user.id} className="list-item" style={{ border: 'none', padding: '8px 0' }}>
                <div className="list-item-content">
                  <div className="flex items-center gap-2">
                    <strong>{user.display_name}</strong>
                    <span className={`badge ${user.is_active ? 'success' : 'error'}`}>
                      {user.is_active ? '活跃' : '停用'}
                    </span>
                  </div>
                  <div className="text-sm text-muted">{user.email}</div>
                </div>
                <div className="text-sm text-muted">{new Date(user.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  variant = 'primary',
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  variant?: 'primary' | 'success' | 'error' | 'info';
}) {
  const colors: Record<string, string> = {
    primary: 'var(--color-primary)',
    success: 'var(--color-success)',
    error: 'var(--color-error)',
    info: 'var(--color-info)',
  };
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--radius-md)',
          background: `${colors[variant]}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors[variant],
        }}
      >
        <Icon size={24} />
      </div>
      <div>
        <div className="text-muted text-sm">{label}</div>
        <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 身份治理
// ═══════════════════════════════════════════════════════════════════
function IdentityGovernance() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({ email: '', display_name: '' });
  const [addTenantId, setAddTenantId] = useState('');
  const [addTenantRole, setAddTenantRole] = useState('MEMBER');

  const loadData = async () => {
    try {
      const [u, t] = await Promise.all([listUsers(), listTenants()]);
      setUsers(u);
      setTenants(t);
    } catch (e) {
      console.error('Failed to load identity data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadData();
    };
    tick();
  }, []);

  const loadUserDetails = async (userId: string) => {
    try {
      const detail = await getUser(userId);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, tenants: detail.tenants } : u))
      );
    } catch (e) {
      console.error('Failed to load user details:', e);
    }
  };

  const toggleExpand = (userId: string) => {
    if (expandedId === userId) {
      setExpandedId(null);
    } else {
      setExpandedId(userId);
      setAddTenantId('');
      setAddTenantRole('MEMBER');
      loadUserDetails(userId);
    }
  };

  const createUser = async () => {
    if (!createForm.email.trim() || !createForm.display_name.trim()) {
      toast.error('邮箱和显示名称为必填项');
      return;
    }
    try {
      const created = await createUserRequest(createForm);
      setUsers([created, ...users]);
      setShowCreate(false);
      setCreateForm({ email: '', display_name: '' });
    } catch (e) {
      toast.error('创建失败: ' + errorMessage(e));
    }
  };

  const toggleActive = async (user: User) => {
    try {
      const updated = await updateUser(user.id, {
        is_active: !user.is_active,
      });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
    } catch (e) {
      toast.error('操作失败: ' + errorMessage(e));
    }
  };

  const deleteUser = async (user: User) => {
    if (
      !(await confirm({
        title: '确定要删除这个 User 吗？',
        message: '此操作不可恢复。',
        danger: true,
        requireText: user.email,
      }))
    )
      return;
    try {
      await deleteUserRequest(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e) {
      toast.error('删除失败: ' + errorMessage(e));
    }
  };

  const addToTenant = async (userId: string) => {
    if (!addTenantId) {
      toast.error('请选择 Tenant');
      return;
    }
    try {
      await addUserToTenant(userId, addTenantId, addTenantRole);
      loadUserDetails(userId);
      setAddTenantId('');
      setAddTenantRole('MEMBER');
    } catch (e) {
      toast.error('添加失败: ' + errorMessage(e));
    }
  };

  const removeFromTenant = async (userId: string, tenantId: string) => {
    if (!(await confirm({ title: '确定将用户从该 Tenant 移除？', danger: true }))) return;
    try {
      await removeUserFromTenant(userId, tenantId);
      loadUserDetails(userId);
    } catch (e) {
      toast.error('移除失败: ' + errorMessage(e));
    }
  };

  const getRoleBadge = (role: string) => {
    const badges: Record<string, string> = {
      OWNER: 'error',
      ADMIN: 'warning',
      MEMBER: 'success',
      VIEWER: 'info',
    };
    return <span className={`badge ${badges[role] || 'info'}`}>{role}</span>;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 style={{ margin: 0 }}>用户与租户身份管理</h3>
        <button className="primary" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={14} /> 新建 User
        </button>
      </div>

      {showCreate && (
        <div className="card">
          <h4>创建新 User</h4>
          <div className="flex flex-col gap-3">
            <input
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              placeholder="Email *"
            />
            <input
              value={createForm.display_name}
              onChange={(e) =>
                setCreateForm({ ...createForm, display_name: e.target.value })
              }
              placeholder="显示名称 *"
            />
            <div className="flex gap-2">
              <button className="primary" onClick={createUser}>
                创建
              </button>
              <button className="secondary" onClick={() => setShowCreate(false)}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div className="empty">
          <h3>暂无 User</h3>
          <p>点击"新建 User"开始创建</p>
        </div>
      ) : (
        <div className="list">
          {users.map((user) => (
            <div
              key={user.id}
              className="list-item"
              style={{ flexDirection: 'column', alignItems: 'stretch' }}
            >
              <div
                className="flex items-center justify-between"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleExpand(user.id)}
              >
                <div className="list-item-content">
                  <div className="flex items-center gap-2">
                    <h3 style={{ margin: 0 }}>{user.display_name}</h3>
                    <span className={`badge ${user.is_active ? 'success' : 'error'}`}>
                      {user.is_active ? '活跃' : '停用'}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0 0' }}>
                    <span className="text-muted">{user.email}</span>
                  </p>
                  <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                    创建于: {new Date(user.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="list-item-actions" style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleActive(user);
                    }}
                    style={{ padding: '6px 12px' }}
                  >
                    {user.is_active ? '停用' : '启用'}
                  </button>
                  <button
                    className="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteUser(user);
                    }}
                    style={{ padding: '6px 12px' }}
                  >
                    <Trash2 size={14} /> 删除
                  </button>
                </div>
              </div>

              {expandedId === user.id && (
                <div className="card mt-4" style={{ margin: '12px 0 0' }}>
                  <h4>Tenant 成员关系</h4>
                  {user.tenants && user.tenants.length > 0 ? (
                    <div className="list" style={{ marginTop: 8 }}>
                      {user.tenants.map((membership: TenantMembership) => (
                        <div
                          key={membership.tenant_id}
                          className="list-item"
                          style={{ border: 'none', padding: '8px 0' }}
                        >
                          <div className="list-item-content">
                            <div className="flex items-center gap-2">
                              <span>{membership.tenant_name || membership.tenant_id}</span>
                              {getRoleBadge(membership.role)}
                            </div>
                          </div>
                          <button
                            className="secondary"
                            onClick={() =>
                              removeFromTenant(user.id, membership.tenant_id)
                            }
                            style={{ padding: '4px 8px', fontSize: '0.85em' }}
                          >
                            移除
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-sm">暂无 Tenant 成员关系</p>
                  )}

                  <div className="flex gap-2 mt-4" style={{ alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label
                        className="text-sm text-muted"
                        style={{ display: 'block', marginBottom: 4 }}
                      >
                        加入 Tenant
                      </label>
                      <select
                        value={addTenantId}
                        onChange={(e) => setAddTenantId(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="">选择 Tenant...</option>
                        {tenants.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.slug})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        className="text-sm text-muted"
                        style={{ display: 'block', marginBottom: 4 }}
                      >
                        角色
                      </label>
                      <select
                        value={addTenantRole}
                        onChange={(e) => setAddTenantRole(e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="primary"
                      onClick={() => addToTenant(user.id)}
                      style={{ padding: '6px 12px', height: 36 }}
                    >
                      添加
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 认证与授权
// ═══════════════════════════════════════════════════════════════════
function AuthManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listUsers(), listTenants()])
      .then(([u, t]) => {
        setUsers(u);
        setTenants(t);
      })
      .catch((e) => console.error('Failed to load auth data:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <h4 className="mb-2">认证方式</h4>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span>Header 认证（X-User-Id / X-Tenant-Slug）</span>
            <span className="badge success">已启用</span>
          </div>
          <div className="flex items-center justify-between">
            <span>JWT Bearer Token</span>
            <span className="badge info">预留</span>
          </div>
          <div className="flex items-center justify-between">
            <span>OAuth2 / SSO</span>
            <span className="badge info">预留</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h4 className="mb-2">角色权限矩阵（按 Tenant）</h4>
        {tenants.length === 0 ? (
          <p className="text-muted">暂无 Tenant</p>
        ) : (
          <div className="flex flex-col gap-4">
            {tenants.map((tenant) => (
              <div key={tenant.id}>
                <h5 style={{ margin: '0 0 8px' }}>{tenant.name}</h5>
                <div className="list">
                  {users
                    .filter((u) => u.tenants?.some((m) => m.tenant_id === tenant.id))
                    .map((u) => {
                      const role = u.tenants!.find((m) => m.tenant_id === tenant.id)!.role;
                      return (
                        <div key={u.id} className="list-item" style={{ border: 'none', padding: '6px 0' }}>
                          <div className="list-item-content">
                            <div className="flex items-center gap-2">
                              <span>{u.display_name}</span>
                              <span className="text-muted text-sm">{u.email}</span>
                            </div>
                          </div>
                          <span className={`badge ${role === 'OWNER' ? 'error' : role === 'ADMIN' ? 'warning' : 'info'}`}>
                            {role}
                          </span>
                        </div>
                      );
                    })}
                  {users.filter((u) => u.tenants?.some((m) => m.tenant_id === tenant.id)).length === 0 && (
                    <p className="text-muted text-sm">该 Tenant 暂无成员</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 应用管理
// ═══════════════════════════════════════════════════════════════════
function AppManagement() {
  const { confirm, dialog } = useConfirm();
  const [apps, setApps] = useState<Application[]>([
    {
      id: 'demo-1',
      name: 'Atelier Web',
      client_id: 'atelier-web',
      redirect_uri: 'http://localhost:25100/callback',
      created_at: new Date().toISOString(),
    },
  ]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', redirect_uri: '' });

  const createApp = () => {
    if (!form.name.trim() || !form.redirect_uri.trim()) return;
    setApps([
      {
        id: `app-${Date.now()}`,
        name: form.name,
        client_id: `client-${Math.random().toString(36).slice(2, 8)}`,
        redirect_uri: form.redirect_uri,
        created_at: new Date().toISOString(),
      },
      ...apps,
    ]);
    setForm({ name: '', redirect_uri: '' });
    setShowCreate(false);
  };

  const deleteApp = async (id: string) => {
    if (!(await confirm({ title: '确定删除该应用？', danger: true }))) return;
    setApps(apps.filter((a) => a.id !== id));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 style={{ margin: 0 }}>OAuth / API 应用管理</h3>
        <button className="primary" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={14} /> 新建应用
        </button>
      </div>

      {showCreate && (
        <div className="card">
          <h4>注册新应用</h4>
          <div className="flex flex-col gap-3">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="应用名称"
            />
            <input
              value={form.redirect_uri}
              onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
              placeholder="Redirect URI"
            />
            <div className="flex gap-2">
              <button className="primary" onClick={createApp}>
                创建
              </button>
              <button className="secondary" onClick={() => setShowCreate(false)}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {apps.length === 0 ? (
        <div className="empty">
          <h3>暂无应用</h3>
        </div>
      ) : (
        <div className="list">
          {apps.map((app) => (
            <div key={app.id} className="list-item">
              <div className="list-item-content">
                <strong>{app.name}</strong>
                <div className="text-sm text-muted">Client ID: {app.client_id}</div>
                <div className="text-sm text-muted">Redirect URI: {app.redirect_uri}</div>
              </div>
              <div className="list-item-actions">
                <button className="danger" onClick={() => deleteApp(app.id)} style={{ padding: '6px 12px' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 组织架构
// ═══════════════════════════════════════════════════════════════════
function OrganizationStructure() {
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([listUsers(), listTenants()])
      .then(([u, t]) => {
        setUsers(u);
        setTenants(t);
      })
      .catch((e) => console.error('Failed to load org data:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const getMembers = (tenantId: string) =>
    users.filter((u) => u.tenants?.some((m) => m.tenant_id === tenantId));

  return (
    <div className="flex flex-col gap-4">
      <h3 style={{ margin: 0 }}>租户 / 组织架构</h3>
      {tenants.length === 0 ? (
        <div className="empty">
          <h3>暂无 Tenant</h3>
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {tenants.map((tenant) => {
            const members = getMembers(tenant.id);
            return (
              <div key={tenant.id} className="card">
                <div className="flex items-center justify-between mb-2">
                  <h4 style={{ margin: 0 }}>{tenant.name}</h4>
                  <span className="badge info">{members.length} 人</span>
                </div>
                <div className="text-sm text-muted mb-2">Slug: {tenant.slug}</div>
                <div className="list">
                  {members.map((u) => {
                    const role = u.tenants!.find((m) => m.tenant_id === tenant.id)!.role;
                    return (
                      <div key={u.id} className="list-item" style={{ border: 'none', padding: '6px 0' }}>
                        <div className="list-item-content">
                          <div className="flex items-center gap-2">
                            <span>{u.display_name}</span>
                            <span className="text-muted text-sm">{u.email}</span>
                          </div>
                        </div>
                        <span className={`badge ${role === 'OWNER' ? 'error' : role === 'ADMIN' ? 'warning' : 'info'}`}>
                          {role}
                        </span>
                      </div>
                    );
                  })}
                  {members.length === 0 && (
                    <p className="text-muted text-sm">暂无成员</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 审计日志
// ═══════════════════════════════════════════════════════════════════
interface AuditEvent {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  actor_id: string | null;
  tenant_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  create: '创建',
  read: '读取',
  update: '更新',
  delete: '删除',
  deploy: '部署',
  login: '登录',
  export: '导出',
  import: '导入',
  config_change: '配置变更',
  runtime_reload: '运行时热更新',
};

function AuditLogs() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listAuditEvents(50), listUsers()])
      .then(([auditData, userList]) => {
        setEvents(auditData.items);
        setTotal(auditData.total);
        setUsers(userList);
      })
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  const actorName = (actorId: string | null) => {
    if (!actorId) return '系统';
    const user = users.find((u) => u.id === actorId);
    return user ? `${user.display_name}（${user.email}）` : actorId;
  };

  const detailText = (log: AuditEvent) => {
    if (!log.details || Object.keys(log.details).length === 0) return '';
    return JSON.stringify(log.details);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h3 style={{ margin: 0 }}>审计日志</h3>
        <p className="text-muted mt-2">加载失败: {error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 style={{ margin: 0 }}>审计日志</h3>
        <span className="text-muted text-sm">共 {total} 条，显示最近 {events.length} 条</span>
      </div>
      {events.length === 0 ? (
        <div className="empty">
          <h3>暂无审计事件</h3>
          <p>本租户内的敏感操作（创建 / 更新 / 部署 / 配置变更等）会自动记录在这里</p>
        </div>
      ) : (
        <div className="list">
          {events.map((log) => (
            <div
              key={log.id}
              className="list-item"
              style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="badge info">
                    {AUDIT_ACTION_LABELS[log.action] || log.action}
                  </span>
                  <strong>{log.entity_type}</strong>
                  {log.entity_id && (
                    <span className="text-muted text-sm">{log.entity_id}</span>
                  )}
                </div>
                <span className="text-muted text-sm">
                  {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted">操作人:</span> {actorName(log.actor_id)}
                {detailText(log) && <span> · {detailText(log)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 消息通知
// ═══════════════════════════════════════════════════════════════════
interface NotificationPrefs {
  email: boolean;
  websocket: boolean;
  sms: boolean;
  deployment: boolean;
  security: boolean;
}

const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  email: true,
  websocket: true,
  sms: false,
  deployment: true,
  security: true,
};

function NotificationSettings() {
  const toast = useToast();
  const [settings, setSettings] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCurrentUserId()
      .then(async (id) => {
        setUserId(id);
        const saved = await loadUserConfig<Partial<NotificationPrefs>>('notification_prefs', id);
        if (saved) {
          setSettings({ ...DEFAULT_NOTIFICATION_PREFS, ...saved });
        }
      })
      .catch((e) => console.error('Failed to load notification prefs:', e))
      .finally(() => setLoading(false));
  }, []);

  const savePrefs = async () => {
    if (!userId) {
      toast.error('无法获取当前用户信息，请刷新后重试');
      return;
    }
    setSaving(true);
    try {
      await saveUserConfig('notification_prefs', settings, userId, '用户消息通知偏好');
      toast.success('通知偏好已保存');
    } catch (e) {
      toast.error('保存失败: ' + errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const items: { key: keyof NotificationPrefs; label: string }[] = [
    { key: 'email', label: '邮件通知' },
    { key: 'websocket', label: '站内 / WebSocket 实时通知' },
    { key: 'sms', label: '短信通知' },
    { key: 'deployment', label: '部署状态变更通知' },
    { key: 'security', label: '安全事件通知' },
  ];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <h3 className="mb-3">消息通知设置</h3>
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <div key={item.key} className="flex items-center justify-between">
            <span>{item.label}</span>
            <label className="switch" style={{ position: 'relative', display: 'inline-block', width: 40, height: 20 }}>
              <input
                type="checkbox"
                checked={settings[item.key]}
                onChange={(e) =>
                  setSettings({ ...settings, [item.key]: e.target.checked })
                }
              />
              <span
                className="slider"
                style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  inset: 0,
                  background: settings[item.key] ? 'var(--color-primary)' : 'var(--border-default)',
                  borderRadius: 20,
                  transition: '0.2s',
                }}
              />
            </label>
          </div>
        ))}
        <button
          className="primary mt-2"
          style={{ alignSelf: 'flex-start' }}
          onClick={savePrefs}
          disabled={saving}
        >
          <Save size={14} /> {saving ? '保存中...' : '保存偏好'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 安全设置
// ═══════════════════════════════════════════════════════════════════
interface SecurityPolicy {
  minPasswordLength: number;
  requireMfa: boolean;
  sessionTimeout: number;
  ipWhitelist: string;
}

const DEFAULT_SECURITY_POLICY: SecurityPolicy = {
  minPasswordLength: 8,
  requireMfa: false,
  sessionTimeout: 30,
  ipWhitelist: '',
};

function SecuritySettings() {
  const toast = useToast();
  const [policy, setPolicy] = useState<SecurityPolicy>(DEFAULT_SECURITY_POLICY);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCurrentUserId()
      .then(async (id) => {
        setUserId(id);
        const saved = await loadUserConfig<Partial<SecurityPolicy>>('security_policy', id);
        if (saved) {
          setPolicy({ ...DEFAULT_SECURITY_POLICY, ...saved });
        }
      })
      .catch((e) => console.error('Failed to load security policy:', e))
      .finally(() => setLoading(false));
  }, []);

  const savePolicy = async () => {
    if (!userId) {
      toast.error('无法获取当前用户信息，请刷新后重试');
      return;
    }
    setSaving(true);
    try {
      await saveUserConfig('security_policy', policy, userId, '用户安全策略配置');
      toast.success('安全策略已保存');
    } catch (e) {
      toast.error('保存失败: ' + errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <h3 className="mb-3">安全设置</h3>
      <p className="text-muted text-sm" style={{ marginTop: -8 }}>
        当前仅持久化保存策略配置（用户级），认证流程尚未强制执行这些策略。
      </p>
      <div className="flex flex-col gap-3">
        <div>
          <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
            最小密码长度
          </label>
          <input
            type="number"
            value={policy.minPasswordLength}
            onChange={(e) =>
              setPolicy({ ...policy, minPasswordLength: parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
        <div>
          <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
            会话超时（分钟）
          </label>
          <input
            type="number"
            value={policy.sessionTimeout}
            onChange={(e) =>
              setPolicy({ ...policy, sessionTimeout: parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="require_mfa"
            type="checkbox"
            checked={policy.requireMfa}
            onChange={(e) => setPolicy({ ...policy, requireMfa: e.target.checked })}
          />
          <label htmlFor="require_mfa" className="text-sm">
            强制开启多因素认证（MFA）
          </label>
        </div>
        <div>
          <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
            登录 IP 白名单（每行一个，留空表示不限制）
          </label>
          <textarea
            rows={4}
            value={policy.ipWhitelist}
            onChange={(e) => setPolicy({ ...policy, ipWhitelist: e.target.value })}
            placeholder="例如：&#10;192.168.1.0/24&#10;10.0.0.5"
          />
        </div>
        <button
          className="primary"
          style={{ alignSelf: 'flex-start' }}
          onClick={savePolicy}
          disabled={saving}
        >
          <Save size={14} /> {saving ? '保存中...' : '保存安全策略'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// 全局设置
// ═══════════════════════════════════════════════════════════════════
function GlobalSettings() {
  const toast = useToast();
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      getResolvedConfig<Partial<SystemSettings>>('platform_settings')
        .then((value) => {
          if (value) {
            setSettings({ ...DEFAULT_SETTINGS, ...value });
          }
        })
        .catch((e) => console.error('Failed to load settings:', e))
        .finally(() => setLoading(false));
    };
    tick();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await saveConfig({
        key: 'platform_settings',
        value: settings,
        scope: 'system',
        description: '平台全局设置',
      });
      toast.success('全局设置已保存');
    } catch (e) {
      toast.error('保存失败: ' + errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 600 }}>
      <h3 className="mb-3">全局设置</h3>
      <div className="flex flex-col gap-3">
        <div>
          <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
            平台名称
          </label>
          <input
            value={settings.platform_name}
            onChange={(e) =>
              setSettings({ ...settings, platform_name: e.target.value })
            }
          />
        </div>
        <div>
          <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
            默认语言
          </label>
          <select
            value={settings.default_language}
            onChange={(e) =>
              setSettings({ ...settings, default_language: e.target.value })
            }
          >
            <option value="zh">中文</option>
            <option value="en">English</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="allow_registration"
            type="checkbox"
            checked={settings.allow_registration}
            onChange={(e) =>
              setSettings({ ...settings, allow_registration: e.target.checked })
            }
          />
          <label htmlFor="allow_registration" className="text-sm">
            允许开放注册
          </label>
        </div>
        <div className="flex gap-2 mt-2">
          <button className="primary" onClick={saveSettings} disabled={saving}>
            <Save size={14} /> {saving ? '保存中...' : '保存设置'}
          </button>
          <button className="secondary" onClick={() => setSettings(DEFAULT_SETTINGS)}>
            重置默认值
          </button>
        </div>
      </div>
    </div>
  );
}
